import { client } from '../gql/client';
import { GET_CLASSIFICATION_BY_ID_OR_ROID } from '../gql/queries/orders/predefinedData';

/**
 * Get classification code from classification ID
 * First tries to read from Apollo cache, if not available fetches from server
 * Maps classification ID to its code ('01', '02', '03')
 *
 * Codes:
 * - '01' = Incidence
 * - '02' = Operation
 * - '03' = Reinvestment
 *
 * @param classificationId - The classification ID from the order
 * @returns Classification code as string ('01', '02', '03'), defaults to '01' if not found
 */
export const getClassificationCode = async (
  classificationId: string
): Promise<string> => {
  try {
    // First try to read from cache
    try {
      const cached = client.readQuery({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: { id: classificationId },
      });

      if (cached?.classification?.classificationCode) {
        return cached.classification.classificationCode
          .toString()
          .padStart(2, '0');
      }
    } catch (cacheError) {
      // Not in cache, continue to fetch from server
    }

    // If not in cache, fetch from server
    const { data } = await client.query({
      query: GET_CLASSIFICATION_BY_ID_OR_ROID,
      variables: { id: classificationId },
      fetchPolicy: 'cache-first',
    });

    if (data?.classification?.classificationCode) {
      // Format code with leading zero (1 -> '01', 2 -> '02', 3 -> '03')
      return data.classification.classificationCode.toString().padStart(2, '0');
    }

    // Default to '01' (Incidence) if not found
    return '01';
  } catch (error) {
    console.error('Error fetching classification code:', error);
    // Default to '01' (Incidence) on error
    return '01';
  }
};

/**
 * Gets the formatted classification name from its ID and a list of classifications.
 * Used to avoid rendering raw UUIDs in the UI (e.g. ContentCardResume).
 */
export const getClassificationName = (
  classificationId?: string | null,
  classifications?: { id?: string; name?: string; classificationCode?: string | number }[]
): string => {
  if (!classificationId) return '';
  if (!classifications || classifications.length === 0) return classificationId;

  const found = classifications.find((c) => c.id === classificationId);
  if (found) {
    if (found.classificationCode) {
      return `${found.classificationCode.toString().padStart(2, '0')} - ${found.name}`;
    }
    return found.name || classificationId;
  }

  return classificationId;
};

